import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Insights, RocketLaunch } from "@mui/icons-material";
import { Close } from "@material-ui/icons";
import { styled } from "@mui/system";
import { theme } from "../../../Customization/Theme";
import { useNotification } from "../../../Hooks/useNotification/useNotification";
import { CoreService } from "../../../Services/Apis/CoreApis";
import { setConfigData } from "../../../Store/Slices/AuthSlice";
import { useAppDispatch } from "../../../Store";

const Backdrop = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(16, 16, 16, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
`;

const PopupContainer = styled(Box)`
  width: 750px;
  background: ${({ theme }: any) => theme.palette.default.white};
  padding: 24px 32px;
  border-radius: 12px;
  box-shadow: ${({ theme }: any) => theme.palette.default.boxShadow};
  position: relative;
`;

const CloseButton = styled(IconButton)`
  position: absolute !important;
  top: 12px;
  right: 12px;
`;

const FeatureBox = styled(Box)`
  background: ${({ theme }: any) => theme.palette.default.darkBlue};
  padding: 20px;
  border-radius: 10px;
  margin-top: 20px;
`;

const FeatureItem = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
`;

const ActionRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 28px;
`;

const SkipButton = styled(Button)`
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 10px 24px !important;
  background: ${({ theme }: any) => theme.palette.default.lotion};
  color: ${({ theme }: any) => theme.palette.default.text};
  border: 1px solid ${({ theme }: any) => theme.palette.default.border};

  &:hover {
    background: ${({ theme }: any) => theme.palette.default.fadeGrey};
  }
`;

const RegisterButton = styled(Button)`
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 10px 24px !important;
`;

// ------------------------------------------------------------------

const MMLitePopup = ({ open = true, onClose, onSkip, onEnable }: any) => {
  // Handle both onEnable and onRegister for backward compatibility
  const { showAlert } = useNotification();
  const dispatch = useAppDispatch();
  // Handle both onEnable for backward compatibility
  const handleRegister = () => {
    CoreService.createMMLiteOnboarding()
      .then((res: any) => {
        showAlert("MMLite Onboarded successfully!", "success");
        CoreService.tokenVerify().then((res: any) => {
          if (res.data) {
            if (res.data.status) {
              dispatch(setConfigData({ payload: res?.data?.token }));
            }
          }
        });
        onClose();
      })
      .catch((err: any) => {
        console.error("MMLite Onboarding error:", err);
        showAlert("MMLite Onboarding failed. Please try again.", "error");
      });
  };

  // Don't render if open is false
  if (!open) return null;

  return (
    <Backdrop>
      <PopupContainer>
        {/* Close Button */}
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>

        {/* Heading */}
        <Typography
          variant="h6"
          fontWeight={600}
          textAlign="center"
          color="primary.dark"
        >
          Do this simple 1 minute registration & improve this campaign's
          Delivery Rate!
        </Typography>

        {/* Sub text */}
        <Typography
          mt={2}
          textAlign="center"
          color="default.text"
          fontSize="14px"
        >
          Meta has launched a new backend system,
          <strong> Marketing Lite APIs</strong>, for sending marketing messages,
          which brings key improvements:
        </Typography>

        {/* Feature Box */}
        <FeatureBox>
          <FeatureItem>
            <RocketLaunch
              sx={{ color: theme.palette.primary.main, mt: "2px" }}
            />
            <Box>
              <Typography color="black" fontWeight={600} fontSize="15px">
                Delivery Rates
              </Typography>
              <Typography color="black" fontSize="13px">
                Expect fewer errors from Meta's per-user message limit,
                enhancing message reach.
              </Typography>
            </Box>
          </FeatureItem>

          <FeatureItem>
            <Insights sx={{ color: theme.palette.primary.main, mt: "2px" }} />
            <Box>
              <Typography color="black" fontWeight={600} fontSize="15px">
                Insights <span style={{ fontSize: "12px" }}>(Coming Soon)</span>
              </Typography>
              <Typography color="black" fontSize="13px">
                If your template includes a CTA button linking to a website with
                Meta Pixel, you’ll gain valuable insights like “add to cart” and
                “purchase” metrics.
              </Typography>
            </Box>
          </FeatureItem>
        </FeatureBox>

        {/* Action Buttons */}
        <ActionRow>
          <SkipButton variant="outlined" onClick={onSkip}>
            Skip & Set Live
          </SkipButton>

          <RegisterButton
            variant="contained"
            color="primary"
            onClick={handleRegister}
          >
            Register for Better Delivery Rate
          </RegisterButton>
        </ActionRow>
      </PopupContainer>
    </Backdrop>
  );
};

export { MMLitePopup };
